import axios from "axios";
import React, { useEffect, useRef, useState } from "react";
import { api } from "../utils/Api";
import {
	Typography,
	Flex,
	Table,
	Button,
	Form,
	Tag,
	notification,
	Select,
	Modal,
	DatePicker,
	Input,
} from "antd";
import Layout from "../layout/Layout";
import {
	CustomSearch,
	CustomSpinner,
	CustomTable,
} from "../utils/CustomComponents";
import { PlusCircleFilled, UnorderedListOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { rankingOptions2 } from "../../constants/Customer";
import { CSVLink } from "react-csv";
const { Text } = Typography;

const CustomerFollowUp = ({ history }) => {
	const [loading, setLoading] = useState(true);
	const [loadingTable, setLoadingTable] = useState(false);
	const [list, setList] = useState([]);
	const [sellerList, setSellerList] = useState([]);
	const [strategyList, setStrategyList] = useState([]);
	const [seller, setSeller] = useState(null);
	const [modalDateOpen, setModalDateOpen] = useState(false);
	const [modalResultOpen, setModalResultOpen] = useState(false);
	const [modalSellerOpen, setModalSellerOpen] = useState(false);
	const followUpData = useRef([]);
	const [ranking, setRanking] = useState("All");
	const refDownload = useRef();
	const [apiNotification, contextHolder] = notification.useNotification();

	const openNotification = (type, message, description) => {
		apiNotification[type]({
			message: message,
			description: description,
		});
	};

	useEffect(() => {
		getSellerList();
		getStrategyList();
	}, []);

	const selectColor = (strategy) => {
		return (
			strategyList.find((item) => item.value === strategy)?.key || "default"
		);
	};

	/**
	 ** Options header (columns)
	 */
	const columns = [
		{
			title: "Customer",
			dataIndex: "customer",
			key: "customer",
			render: (value, values) => {
				return <Text strong>{value}</Text>;
			},
			onCell: (record) => {
				return {
					style: {
						background: selectColor(record.strategy),
					},
				};
			},
		},
		{
			title: "Ranking",
			dataIndex: "ranking",
			key: "ranking",
			render: (value) => {
				let color = "";
				switch (value) {
					case "A":
						color = "purple";
						break;
					case "B":
						color = "blue";
						break;
					case "C":
						color = "green";
						break;
					case "D":
						color = "red";
						break;
					default:
						color = "default";
						break;
				}
				return <Tag color={color}>{value || "None"}</Tag>;
			},
		},
		{
			title: "Historic Volume",
			dataIndex: "totalLoadYD3",
			key: "totalLoadYD3",
			render: (value) => {
				return <Text>{value}</Text>;
			},
		},
		{
			title: "Last Pour",
			dataIndex: "lastPour",
			key: "lastPour",
			render: (value) => {
				if (!value) return <Tag color="default">None</Tag>;

				const formatDate = new Intl.DateTimeFormat("en-US", {
					month: "numeric",
					year: "numeric",
					day: "numeric",
					hour: "2-digit",
					minute: "2-digit",
					second: "2-digit",
				}).format(new Date(value));

				return <Tag color={"default"}>{formatDate}</Tag>;
			},
		},
		{
			title: "Strategy",
			dataIndex: "strategy",
			key: "strategy",
			editable: true,
		},
		{
			title: "Contact Name",
			dataIndex: "contactName",
			key: "contactName",
			render: (value) => {
				return <Text>{value}</Text>;
			},
		},
		{
			title: "Contact Phonecall",
			dataIndex: "sms",
			key: "sms",
			render: (value) => {
				return <Text>{value}</Text>;
			},
		},
		{
			title: "Phonecall Result",
			dataIndex: "phoneCallResult",
			key: "phoneCallResult",
			editable: true,
		},
		{
			title: "Next Pour Estimated Date",
			dataIndex: "nextPourEstimationDate",
			key: "nextPourEstimationDate",
			editable: true,
		},
		{
			title: "Seller",
			dataIndex: "seller",
			key: "seller",
			editable: true,
		},
	];

	const EditableCell = ({
		editMode,
		dataIndex,
		record,
		handleSave,
		children,
		sellerList,
		followUpData,
		setModalSellerOpen,
		setModalResultOpen,
		setModalDateOpen,
		...restProps
	}) => {
		const [form] = Form.useForm();

		const email = localStorage.getItem("email");

		const sellerDisabled =
			email !== "eliud@gmail.com" &&
			email !== "ec@57concrete.com" &&
			email !== "rl@57concrete.com";

		const onSaveValue = (value, field = dataIndex) => {
			handleSave(
				{
					...record,
					[field]: value,
				},
				field,
			);
		};

		return (
			<td {...restProps}>
				{editMode ? (
					<Form form={form} initialValues={{ [dataIndex]: record[dataIndex] }}>
						<Form.Item
							style={{ margin: 0 }}
							name={dataIndex}
							rules={[{ required: false }]}
						>
							{dataIndex === "seller" ? (
								<Flex gap="small">
									<Select
										style={{ width: "100%" }}
										value={record.seller}
										options={sellerList}
										disabled={sellerDisabled}
										onChange={(value) => onSaveValue(value, "seller")}
										allowClear={false}
									/>

									<Button
										icon={<UnorderedListOutlined />}
										onClick={() => {
											followUpData.current = record;
											setModalSellerOpen(true);
										}}
									/>
								</Flex>
							) : dataIndex === "strategy" ? (
								<Select
									style={{ width: "100%" }}
									value={record.strategy}
									options={strategyList}
									onChange={(value) => onSaveValue(value, "strategy")}
									allowClear={false}
								/>
							) : dataIndex === "phoneCallResult" ? (
								<Flex gap="small">
									<Input
										defaultValue={record.phoneCallResult}
										onBlur={(e) =>
											onSaveValue(e.target.value, "phoneCallResult")
										}
									/>

									<Button
										icon={<UnorderedListOutlined />}
										onClick={() => {
											followUpData.current = record;
											setModalResultOpen(true);
										}}
									/>
								</Flex>
							) : dataIndex === "nextPourEstimationDate" ? (
								<Flex gap="small">
									<DatePicker
										style={{ width: "100%" }}
										value={
											record.nextPourEstimationDate
												? dayjs(record.nextPourEstimationDate)
												: null
										}
										format="MM/DD/YYYY"
										onChange={(date) =>
											onSaveValue(
												date ? date.toDate() : null,
												"nextPourEstimationDate",
											)
										}
										allowClear={false}
									/>

									<Button
										icon={<UnorderedListOutlined />}
										onClick={() => {
											followUpData.current = record;
											setModalDateOpen(true);
										}}
									/>
								</Flex>
							) : (
								children
							)}
						</Form.Item>
					</Form>
				) : (
					children
				)}
			</td>
		);
	};

	const handleSave = async (updatedCustomer, field) => {
		await updateCustomer(updatedCustomer, field);
		const updatedList = list.map((item) => {
			if (item.key !== updatedCustomer.key) return item;
			return updatedCustomer;
		});
		setList(updatedList);
	};

	const updateCustomer = async (customer, field) => {
		setLoadingTable(true);
		const url = api + `/customer/update/prospection/${customer.id}`;
		const token = localStorage.getItem("token");
		axios.defaults.headers.common = { Authorization: `Bearer ${token}` };

		let body = {};

		if (field === "seller") {
			body.prospectionSeller = customer.seller;
		}

		if (field === "strategy") {
			body.strategy = customer.strategy;
		}
		if (field === "phoneCallResult") {
			body.phoneCallResult = customer.phoneCallResult;
		}
		if (field === "nextPourEstimationDate") {
			body.nextPourEstimationDate = dayjs(
				customer.nextPourEstimationDate,
			).toDate();
		}

		try {
			const { data } = await axios.patch(url, body);
			if (data.success) {
				setLoadingTable(false);
				if (field === "seller") {
					getCustomerList();
				}
			} else {
				setLoadingTable(true);
			}
		} catch (err) {
			setLoadingTable(false);
			console.error(err.message);
			return null;
		}
	};

	const mergedColumns = columns.map((col) => {
		if (!col.editable) {
			return col;
		}
		return {
			...col,
			onCell: (record) => ({
				record,
				editMode: true,
				dataIndex: col.dataIndex,
				title: col.title,
				handleSave: handleSave,
			}),
		};
	});

	const getSellerList = async () => {
		setLoading(true);

		const url = api + "/user/list/57Concrete/Seller";
		const token = localStorage.getItem("token");
		axios.defaults.headers.common = { Authorization: `Bearer ${token}` };

		try {
			const { data } = await axios.get(url);
			if (data.success) {
				let list = [];
				data.payload.map((item, index) => {
					list.push({
						key: index,
						value: item._id,
						label: item.name,
					});
				});
				setSellerList(list);
				const currentSeller = list.find(
					(seller) => seller.value === localStorage.getItem("userId"),
				);
				if (currentSeller) {
					setSeller(currentSeller.value);
				}
			} else {
				openNotification("error", "Error", data.message);
			}
			setLoading(false);
		} catch (err) {
			openNotification("error", "Error", err.message);
			return null;
		}
	};

	const getStrategyList = async () => {
		setLoading(true);

		const url = api + "/strategy/get/list";
		const token = localStorage.getItem("token");
		axios.defaults.headers.common = { Authorization: `Bearer ${token}` };

		try {
			const { data } = await axios.get(url);
			if (data.success) {
				let list = [];
				data.payload.map((item, index) => {
					list.push({
						key: item.color,
						value: item.name,
						label: item.name,
					});
				});
				setStrategyList(list);
			} else {
				openNotification("error", "Error", data.message);
			}
			setLoading(false);
		} catch (err) {
			openNotification("error", "Error", err.message);
			return null;
		}
	};

	useEffect(() => {
		if (seller && ranking) {
			getCustomerList();
		}
	}, [seller, ranking]);

	const getCustomerList = async () => {
		setLoading(true);

		const url =
			api + `/customer/list/dates/byseller/${seller}?ranking=${ranking}`;
		const token = localStorage.getItem("token");
		axios.defaults.headers.common = { Authorization: `Bearer ${token}` };

		try {
			const { data } = await axios.get(url);
			let list = [];
			if (data.success) {
				data.payload.map((item, index) => {
					list.push({
						key: index,
						id: item._id,
						customer: `${item.customerNumber} ${item.name}`,
						totalLoadYD3: item.totalLoadYD3 || 0,
						lastPour: item.job ? item.job.scheduledDate : null,
						seller: item.prospectionSeller._id,
						strategy: item.strategy || "Not Assigned",
						contactName: item.contactName || null,
						sms: item.sms || null,
						phoneCallResult: item.phoneCallResult || null,
						nextPourEstimationDate: item.nextPourEstimationDate || null,
						ranking: item.ranking || "Not Assigned",
					});
				});
				setList(list);
			} else {
				openNotification("error", "Error", data.message);
			}
			setLoading(false);
		} catch (err) {
			openNotification("error", "Error", err.message);
			return null;
		}
	};

	const RenderTable = () => {
		return (
			<Flex gap={"middle"} vertical>
				<Table
					columns={mergedColumns}
					dataSource={list}
					components={{
						body: {
							cell: (props) => (
								<EditableCell
									{...props}
									sellerList={sellerList}
									followUpData={followUpData}
									setModalSellerOpen={setModalSellerOpen}
									setModalResultOpen={setModalResultOpen}
									setModalDateOpen={setModalDateOpen}
								/>
							),
						},
					}}
					pagination={{
						position: ["bottomRight"],
						pageSizeOptions: [10, 20, 50, 100, 250],
						defaultPageSize: 10,
						showSizeChanger: true,
					}}
					scroll={{
						x: true,
					}}
					style={{
						minWidth: "100%",
					}}
					loading={loadingTable}
				/>
			</Flex>
		);
	};

	const onChangeSeller = (value) => {
		setSeller(value);
		if (!value) {
			setList([]);
		}
	};

	const onChangeRanking = (value) => {
		setRanking(value);
	};

	const handleAddStrategy = () => {
		history.push("/strategy/list");
	};

	const columnsCSV = [
		{ key: "customer", label: "Customer" },
		{ key: "contactName", label: "Contact Name" },
		{ key: "sms", label: "Phone Number" },
	];

	const csvData = () => {
		return list.map((customer) => {
			return {
				customer: customer.customer,
				contactName: customer.contactName || "",
				sms: customer.sms || "",
			};
		});
	};

	const downloadPDF = async () => {
		const url = api + `/customer/pdf/byseller/${seller}?ranking=${ranking}`;
		const token = localStorage.getItem("token");
		axios.defaults.headers.common = { Authorization: `Bearer ${token}` };

		try {
			const { data } = await axios.post(url);
			if (data.success) {
				const link = document.createElement("a");
				link.href = data.payload;
				link.target = "_blank";
				document.body.appendChild(link);
				link.click();
				link.remove();
			} else {
				openNotification("error", "Error", data.message);
			}
		} catch (err) {
			openNotification("error", "Error", err.message);
			return null;
		}
	};

	const RenderSellers = () => {
		return (
			<Flex gap={"large"} align={"center"} justify="space-between">
				<CustomSearch
					loading={loading}
					allowClearPicker={true}
					labelPicker={"Seller"}
					optionsPicker={sellerList}
					pickerWidth={"300px"}
					pickerValue={seller}
					onChangePicker={onChangeSeller}
					allowClearPicker2={false}
					labelPicker2={"Ranking"}
					optionsPicker2={rankingOptions2}
					pickerWidth2={"300px"}
					pickerValue2={ranking}
					onChangePicker2={onChangeRanking}
					disabledPicker2={!seller}
				/>
				<Flex gap={"small"} align={"center"}>
					<CSVLink
						data={csvData()}
						headers={columnsCSV}
						filename={"57concrete-customer-list.csv"}
						hidden
						ref={refDownload}
					>
						Download report
					</CSVLink>
					<Button
						size="large"
						type="primary"
						onClick={(e) => refDownload.current.link.click()}
						loading={loading}
						disabled={list.length === 0}
					>
						Download CSV
					</Button>
					<Button
						size="large"
						type="primary"
						onClick={downloadPDF}
						loading={loading}
						disabled={list.length === 0}
					>
						Download PDF
					</Button>
					<Button
						icon={<PlusCircleFilled />}
						type="primary"
						onClick={handleAddStrategy}
						size="large"
					>
						Add Strategy
					</Button>
				</Flex>
			</Flex>
		);
	};

	const ModalViewResult = () => {
		return (
			<Modal
				title="Phonecall Result History"
				open={modalResultOpen}
				onCancel={() => setModalResultOpen(false)}
				cancelButtonProps={{ style: { display: "none" } }}
				okButtonProps={{ style: { display: "none" } }}
				width={1000}
			>
				<ShowHistory data={followUpData.current} type="result" />
			</Modal>
		);
	};

	const ModalViewNextPour = () => {
		return (
			<Modal
				title="Pour Estimated Date History"
				open={modalDateOpen}
				onCancel={() => setModalDateOpen(false)}
				cancelButtonProps={{ style: { display: "none" } }}
				okButtonProps={{ style: { display: "none" } }}
				width={1000}
			>
				<ShowHistory data={followUpData.current} type="date" />
			</Modal>
		);
	};

	const ModalViewSeller = () => {
		return (
			<Modal
				title="Seller History"
				open={modalSellerOpen}
				onCancel={() => setModalSellerOpen(false)}
				cancelButtonProps={{ style: { display: "none" } }}
				okButtonProps={{ style: { display: "none" } }}
				width={1000}
			>
				<ShowHistory data={followUpData.current} type="seller" />
			</Modal>
		);
	};

	return (
		<Layout title="Follow Up" description="Dashboard" type="large">
			<Flex vertical gap={"large"}>
				<RenderSellers />
				{loading ? <CustomSpinner /> : <RenderTable />}
			</Flex>
			{contextHolder}
			<ModalViewResult />
			<ModalViewNextPour />
			<ModalViewSeller />
		</Layout>
	);
};

export default CustomerFollowUp;

const ShowHistory = ({ data, type }) => {
	const [loading, setLoading] = useState(true);
	const [history, setHistory] = useState([]);
	const [columns, setColumns] = useState([]);

	const [apiNotification, contextHolder] = notification.useNotification();

	const openNotification = (type, message, description) => {
		apiNotification[type]({
			message: message,
			description: description,
		});
	};

	useEffect(() => {
		if (data) {
			getCustomerData();
		}
	}, [data]);

	const getCustomerData = async () => {
		setLoading(true);

		const url = api + `/customer/data/followup/${data.id}`;
		const token = localStorage.getItem("token");
		axios.defaults.headers.common = { Authorization: `Bearer ${token}` };

		try {
			const { data } = await axios.get(url);
			if (data.success) {
				let list = [];
				if (type === "seller") {
					data.payload.prospectionSellerHistory.map((item, index) => {
						list.push({
							key: index,
							seller: item.seller ? item.seller.name : "Unknown",
							user: item.updatedBy ? item.updatedBy.name : "Unknown",
							date: dayjs(item.createdAt).format("MM/DD/YYYY hh:mm A"),
						});
					});
				} else if (type === "date") {
					data.payload.nextPourEstimationDateHistory.map((item, index) => {
						list.push({
							key: index,
							user: item.user ? item.user.name : "Unknown",
							date: dayjs(item.date).format("MM/DD/YYYY"),
							createdAt: dayjs(item.createdAt).format("MM/DD/YYYY hh:mm A"),
						});
					});
				} else if (type === "result") {
					data.payload.phoneCallResultHistory.map((item, index) => {
						list.push({
							key: index,
							date: dayjs(item.createdAt).format("MM/DD/YYYY hh:mm A"),
							user: item.user ? item.user.name : "Unknown",
							comment: item.comment,
						});
					});
				}

				setHistory(list);
				if (type === "result") {
					setColumns([
						{
							title: "Date",
							dataIndex: "date",
							key: "date",
							render: (value) => {
								return <Text>{value}</Text>;
							},
						},
						{
							title: "User",
							dataIndex: "user",
							key: "user",
							render: (value) => {
								return <Text>{value}</Text>;
							},
						},
						{
							title: "Comment",
							dataIndex: "comment",
							key: "comment",
							render: (value) => {
								return <Text>{value}</Text>;
							},
						},
					]);
				} else if (type === "seller") {
					setColumns([
						{
							title: "Date",
							dataIndex: "date",
							key: "date",
							render: (value) => {
								return <Text>{value}</Text>;
							},
						},
						{
							title: "User",
							dataIndex: "user",
							key: "user",
							render: (value) => {
								return <Text>{value}</Text>;
							},
						},
						{
							title: "Seller Changed",
							dataIndex: "seller",
							key: "seller",
							render: (value) => {
								return <Text>{value}</Text>;
							},
						},
					]);
				} else if (type === "date") {
					setColumns([
						{
							title: "Date",
							dataIndex: "createdAt",
							key: "createdAt",
							render: (value) => {
								return <Text>{value}</Text>;
							},
						},
						{
							title: "User",
							dataIndex: "user",
							key: "user",
							render: (value) => {
								return <Text>{value}</Text>;
							},
						},
						{
							title: "Date Estimated",
							dataIndex: "date",
							key: "date",
							render: (value) => {
								return <Text>{value}</Text>;
							},
						},
					]);
				}
			} else {
				openNotification("error", "Error", data.message);
			}
			setLoading(false);
		} catch (err) {
			openNotification("error", "Error", err.message);
			return null;
		}
	};

	return (
		<div>
			{loading ? (
				<CustomSpinner />
			) : (
				<CustomTable data={history} columns={columns} isScroll={false} />
			)}
			{contextHolder}
		</div>
	);
};
